import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Book } from '../types';
import { getGraphQLClient, GET_DRAFT_BOOKS, DELETE_BOOK } from '../lib/graphql';
import { Button, Spinner } from '../components/common';

export default function DraftsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadDrafts = async () => {
    try {
      const client = getGraphQLClient();
      const data = await client.request<{ draftBooks: Book[] }>(GET_DRAFT_BOOKS);
      setDrafts(data.draftBooks);
    } catch (error) {
      console.error('Failed to load drafts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDrafts();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm(t('books.deleteConfirm'))) return;

    try {
      const client = getGraphQLClient();
      await client.request(DELETE_BOOK, { id });
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    } catch (error) {
      console.error('Failed to delete draft:', error);
    }
  };

  const handleContinue = (id: string) => {
    navigate(`/books/new/${id}/basics`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link
            to="/books"
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('books.drafts')}
          </h1>
        </div>
      </div>

      {drafts.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <svg
            className="w-24 h-24 mx-auto mb-6 text-gray-300 dark:text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('books.noDrafts')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{t('books.noDraftsDescription')}</p>
          <Link to="/books">
            <Button variant="secondary">{t('common.back')}</Button>
          </Link>
        </motion.div>
      ) : (
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
          className="space-y-4"
        >
          {drafts.map((draft) => (
            <motion.div
              key={draft.id}
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0 },
              }}
              className="card p-4 flex items-center justify-between"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t(`bookTypes.${draft.bookType?.toLowerCase()}`)}
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <span className="text-sm text-gray-400 dark:text-gray-500">
                    {draft.themes
                      ?.slice(0, 3)
                      .map((theme) => t(`themes.${theme}`, theme))
                      .join(', ')}
                    {(draft.themes?.length || 0) > 3 && '...'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('books.createdAt')}: {new Date(draft.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="primary" size="sm" onClick={() => handleContinue(draft.id)}>
                  {t('books.continue')}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => handleDelete(draft.id)}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </Button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
